/* eslint-disable no-redeclare */
import {
  DomainSessionId,
  DomainUserId,
  HealthcareSessionId,
  HealthcareUserId,
  Integer,
  JourneyAlias,
  JourneyId,
  ProductType,
} from '@hc-frontend/core-types';
import { z } from 'zod';

import { PlateName } from './plate-name';
import { PartialSurveyAnswers } from './survey-answers';

export const Transition = z.object({
  plate: PlateName,
  validate: z.boolean(),
  questions: PartialSurveyAnswers,
});

export type Transition = z.infer<typeof Transition>;

export const PlatePath = z.enum([
  'survey',
  'survey/dob-category',
  'survey/coverage-urgency',
  'survey/gender-dob',
  'survey/gender',
  'survey/dob',
  'survey/enrolled-part-a-part-b',
  'survey/kind-of-plan',
  'survey/personal-information',
  'survey/zip-code',
  'survey/visit-purpose',
  'quotes',
  'survey/redirect-aca-healthcare-com',
  'survey/coverage-duration',
  'survey/change-pharmacy',
  'survey/estimated-spending',
  'survey/household-size',
  'survey/household-income',
  'survey/redirect-typ-health',
  'survey/income-subsidy',
  'survey/qle',
  'survey/redirect-stm-healthcare-com',
  'survey/insure-who',
  'survey/redirect-medicare-healthcare-com',
  'survey/spouse-children',
  'survey/immediate-coverage',
  'survey/tobacco',
  'survey/senior-check',
  'survey/redirect-typ',
  // Health
  'survey/redirect-aca-healthcare-com',
  'survey/gender-dob',
  'survey/coverage-duration',
  'survey/change-pharmacy',
  'survey/estimated-spending',
  'survey/zip-code',
  'survey/household-income',
  'survey/household-size-income',
  'survey/redirect-typ-health',
  'survey/income-subsidy',
  'survey/qle',
  'survey/redirect-stm-healthcare-com',
  'survey/insure-who',
  'survey/redirect-medicare-healthcare-com',
  'survey/spouse-children',
  'survey/immediate-coverage',
  'survey/personal-information',
  'survey/tobacco',
  'survey/senior-check',
  'survey/first-time-enrollment',

  // Life Insurance
  'survey/insurance-options',
  'survey/medical-history',
  'survey/height-weight',
  'survey/family-medical-history',
  'survey/annual-income',
  'survey/employment-status',
  'survey/health-assessment',
  'survey/lifestyle-activities',
  'survey/redirect-typ-life',
  'survey/personal-information',
]);

export type PlatePath = z.infer<typeof PlatePath>;

export const Plate = z.object({
  name: PlateName,
  order: Integer,
  pathname: PlatePath.optional(),
  transitions: Transition.array(),
});

export type Plate = z.infer<typeof Plate>;

export const Journey = z.object({
  id: JourneyId,
  plates: z.record(PlateName, Plate),
  start: PlateName,
});

export type Journey = z.infer<typeof Journey>;

export const MoveToPlateOptions = z.object({
  plate: Plate.optional(),
  journeyAlias: JourneyAlias,
  targetsExternalApp: z.boolean().optional(),
});

export type MoveToPlateOptions = z.infer<typeof MoveToPlateOptions>;

export const GetNextPlateOptions = z.object({
  currentPlate: PlateName.optional(),
  journey: Journey,
  answers: PartialSurveyAnswers,
});

export type GetNextPlateOptions = z.infer<typeof GetNextPlateOptions>;

export const MoveToNextPlateOptions = z.object({
  productType: ProductType,
  journeyAlias: JourneyAlias,
  currentPlate: PlateName.optional(),
  surveyAnswers: PartialSurveyAnswers.optional(),
  userId: HealthcareUserId,
  sessionId: HealthcareSessionId,
  domainUserId: DomainUserId.optional(),
  domainSessionId: DomainSessionId.optional(),
});

export type MoveToNextPlateOptions = z.infer<typeof MoveToNextPlateOptions>;
